import {
  numberEqual,
  objectEqual,
  optionalEqual,
  stringEqual,
} from "@redotech/util/equal";

// the double underscore make an attribute hidden on the storefront.
// we noticed this causing issues with upcart
// it also makes it so we can't detect for ourselves whether the attribute is set or not.
// we're switching to the new attribute with no underscore
export const REDO_LOADED_ON_CART_ATTRIBUTE_KEY_DEPRECATED =
  "__redo_loaded_on_cart";
export const REDO_LOADED_ON_CART_ATTRIBUTE_KEY = "redo_loaded_on_cart";
export const REDO_OPTED_IN_FROM_CART_ATTRIBUTE_KEY = "redo_opted_in_from_cart";
export const REDO_METADATA_ATTRIBUTE_KEY = "redo_metadata";
// Written by the storefront checkout-probability context the first time the
// intent/hazard system runs for a cart. Used by the order webhook to bill
// orders whose checkout was touched by the intent system even if no Redo
// shipping line was chosen. Scrubbed from the order before the merchant sees it.
export const REDO_INTENT_ACTIVE_CART_ATTRIBUTE_KEY = "redo_intent_active";
// HSA/FSA questionnaire session token, written by the storefront PDP modal
// (`redo/shopify/extension/src/hsa-questionnaire/`) when the shopper first
// opens the modal. Persisting on the cart attribute lets the modal
// reload-resume across page navigations within the same cart, and lets the
// checkout-time questionnaire entry point (future work) pick up answers
// the shopper already gave on the PDP.
export const REDO_HSA_SESSION_TOKEN_ATTRIBUTE_KEY = "redo_hsa_session_token";
// Set per-cart (future "Buy with HSA" button) to ask the HSA payment-
// customization Shopify Function
// (`redo/shopify/app/hsa-payment-customization/`) to sort the HSA/FSA payment
// method to the top of checkout for this cart only, independent of the
// merchant-wide `reorderHsaPaymentMethodToTop` setting. The string value here is
// a contract with the Rust function, which reads the cart attribute by this
// literal key. Scrubbed from the order by the order webhook.
export const REDO_HSA_PAYMENT_PRIORITY_ATTRIBUTE_KEY =
  "redo_hsa_payment_priority";
// Set per-cart by "Buy with HSA" to make the payment-customization Function hide
// non-HSA methods. One-shot — expired on storefront return; scrubbed from orders.
export const REDO_HSA_PAYMENT_EXCLUSIVITY_ATTRIBUTE_KEY =
  "redo_hsa_payment_exclusivity";

export interface RedoMetadata {
  cartToken?: string;
  optedTimestamp?: number;
  coveragePriceCache?: CoveragePriceCache;
  stagecoachDecision?: StagecoachDecisionMetadata;
  merchandiseOutOfStockError?: boolean;
  /**
   * Anti-discount pricing for the Redo coverage line, consumed by the
   * `dynamic-coverage-transform` Shopify Function. Encoded as
   * `[cartHash]:[base64(price)]` so the function can verify the price was
   * computed for the current cart before applying it. See
   * `./cart-hash.ts`.
   */
  coverageTransformFunctionInfo?: string;
}

export interface StagecoachDecisionMetadata {
  cartHash: string;
  packageProtectionPrice: number;
  modelVersionId?: string;
  betaSampleIndex?: number;
  validationMode: "detect_only";
}

export const MERCHANDISE_OUT_OF_STOCK_CODE = "MERCHANDISE_OUT_OF_STOCK";
export const MERCHANDISE_ERROR = "merchandiseError";

export interface CoveragePriceCache {
  cache: Record<string, CoveragePriceCacheValue>; // cart hash -> coverage pricing info
  lastUpdatedTimestamp: number;
  latestCartHash: string;
  // Stagecoach config for recomputing ML pricing on cache miss. Stamped by
  // the storefront; the checkout-UI uses this to reconstruct the model when
  // the checkout autocheck doesn't include treatment-level ML models.
  stagecoachInferenceConfig?: StagecoachInferenceConfigForCheckout;
  /**
   * Numeric Shopify product ids of the team's Redo coverage products, stamped
   * from autocheck so the checkout-UI can id-match without a network call.
   */
  coverageProductIds?: string[];
}

// Minimal stagecoach config the checkout-UI needs to recompute pricing on
// cart changes. Subset of StagecoachInferenceConfig from the model package.
export interface StagecoachInferenceConfigForCheckout {
  inferenceMode: "disabled" | "shadow" | "stub" | "active";
  stagecoachInferenceConfigVersion: number;
  modelVersionId: string;
  betaSampleIndex: number;
  betaVector: number[];
  posteriorSubsample?: { betaSampleIndex: number; betaVector: number[] }[];
  stagecoachFeatureOrdering: (
    | "intercept"
    | "price_ratio"
    | "cart_value"
    | "log_cart_value"
    | "intent_score"
    | "price_ratio_x_cart_value"
  )[];
  featureComputationRules?: {
    priceRatioFormula?: string;
    logCartValueEpsilon?: number;
    missingIntentStrategy?: "null" | "zero" | "global_mean";
  };
  conversionVsPricingTradeoffConfig?: { alpha: number };
  priceSearchConfig?: { priceCandidateTotal?: number };
  inferenceFeatureScaling?: Record<string, { mean: number; std: number }>;
  expectedClaimCostDollars?: number;
}

export interface CoveragePriceCacheValue {
  returns?: { price: number };
  packageProtection?: {
    price: number;
    modelVersionId?: string;
    betaSampleIndex?: number;
  };
  finalSaleReturns?: { price: number };
}

export const isCoveragePriceCacheEqual = objectEqual<CoveragePriceCache>({
  cache: objectEqual<CoveragePriceCacheValue>({
    returns: objectEqual<CoveragePriceCacheValue["returns"]>({
      price: numberEqual,
    }),
    packageProtection: objectEqual<
      CoveragePriceCacheValue["packageProtection"]
    >({
      price: numberEqual,
      modelVersionId: optionalEqual(stringEqual),
      betaSampleIndex: optionalEqual(numberEqual),
    }),
    finalSaleReturns: objectEqual<CoveragePriceCacheValue["finalSaleReturns"]>({
      price: numberEqual,
    }),
  }),
  lastUpdatedTimestamp: numberEqual,
  latestCartHash: stringEqual,
});

export function parseRedoMetadata(rawRedoMetadata: string): RedoMetadata {
  return JSON.parse(rawRedoMetadata);
}

export function parseCoveragePriceCache(
  rawCoveragePriceCache: string,
): CoveragePriceCache {
  return JSON.parse(rawCoveragePriceCache);
}

export function safeParseRedoMetadata(
  redoMetadataString: string | undefined,
): RedoMetadata | undefined {
  if (!redoMetadataString) {
    return undefined;
  }
  try {
    const redoMetadata: RedoMetadata | undefined = redoMetadataString
      ? JSON.parse(redoMetadataString)
      : undefined;
    return redoMetadata;
  } catch (e) {
    console.error("error parsing redo metadata", e);
    return undefined;
  }
}
